import { useMutation, type MutateOptions } from '@tanstack/react-query';
import { postStorageUpload } from '@blazeteams/sdk';
import { useAuthStore } from '@/store/auth-store';
import { sessionRevision, sessionScope, sessionTenantId, requireCurrentSession } from '@/features/auth/session-scope';
import { getLocaleSnapshot } from '@/lib/i18n/runtime';
import { MAX_UPLOAD_SIZE, UploadError, uploadUUID, readUpload, uploadProblem, type UploadResult } from './upload-contract';
export type { UploadResult } from './upload-contract';

interface UploadIntent { file: File; revision: number; scope: string; tenant: string; language: string }
function capture(file: File): UploadIntent {
  const state = useAuthStore.getState();
  return { file, revision: sessionRevision(), scope: sessionScope(state), tenant: sessionTenantId(state), language: getLocaleSnapshot().locale };
}
async function execute(intent: UploadIntent): Promise<UploadResult> {
  requireCurrentSession(intent.revision);
  if (intent.scope === 'anonymous' || !uploadUUID(intent.tenant)) throw new UploadError('unauthorized', false);
  if (!(intent.file instanceof File)) throw new UploadError('required', false);
  if (intent.file.size > MAX_UPLOAD_SIZE) throw new UploadError('too_large', false);
  let result;
  try { result = await postStorageUpload({ body: { file: intent.file }, headers: { 'Accept-Language': intent.language } }); }
  catch { requireCurrentSession(intent.revision); throw new UploadError(); }
  requireCurrentSession(intent.revision);
  if (result.error || result.response?.status !== 200) throw uploadProblem(result.error, result.response?.status ?? 0);
  return readUpload(result.data, intent.tenant);
}
/** Parallel uploads keep one immediate frozen intent per file, independent of
 * TanStack observer callbacks. The shared SDK never replays POST after401. */
export function uploadFileToStorage(file: File): Promise<UploadResult> { return execute(capture(file)); }

export function useUploadFile() {
  const mutation = useMutation<UploadResult, Error, UploadIntent>({ mutationFn: execute, retry: false, gcTime: 0 });
  const guard = (options?: MutateOptions<UploadResult, Error, UploadIntent>): MutateOptions<UploadResult, Error, UploadIntent> => ({
    onSuccess: (...args) => { if (args[1].revision === sessionRevision()) options?.onSuccess?.(...args); },
    onError: (...args) => { if (args[1].revision === sessionRevision()) options?.onError?.(...args); },
    onSettled: (...args) => { if (args[2].revision === sessionRevision()) options?.onSettled?.(...args); },
  });
  return { ...mutation, mutate: ({ file }: { file: File }, options?: MutateOptions<UploadResult, Error, UploadIntent>) => mutation.mutate(capture(file), guard(options)), mutateAsync: async ({ file }: { file: File }, options?: MutateOptions<UploadResult, Error, UploadIntent>) => {
    const intent = capture(file), receipt = await mutation.mutateAsync(intent, guard(options)); requireCurrentSession(intent.revision); return receipt;
  } };
}

/**
 * Map a MIME type to the inbox `message_type` enum the backend expects.
 *
 * Anything that isn't an image/video/audio falls through to `'document'`,
 * which matches the WhatsApp/IG/Messenger handling on the backend (PDFs,
 * docx, xlsx, txt all ride the document rail).
 */
export function mimeToMessageType(
  mime: string,
): 'image' | 'video' | 'audio' | 'document' {
  const lower = (mime ?? '').toLowerCase();
  if (lower.startsWith('image/')) return 'image';
  if (lower.startsWith('video/')) return 'video';
  if (lower.startsWith('audio/')) return 'audio';
  return 'document';
}
